/**
 * AppLayout — dense dashboard shell (Feb 2026 · Directive §1 rebuild).
 *
 * Desktop:
 *   • Collapsible <Sidebar/> (250px ↔ 64px) fixed on the left.
 *   • Sticky top header (h-14) with Command Palette, Breadcrumb,
 *     NotificationBell and ThemeToggle. Sidebar collapse control lives
 *     inside the Sidebar itself; Logout is the last item in the Sidebar.
 *   • Content wrapper's padding-left animates with the sidebar width.
 *
 * Mobile:
 *   • No sidebar; hamburger opens a <Sheet/> that renders the same
 *     Sidebar in expanded mode.
 */
import { useState, useCallback, useEffect } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { Menu, Moon, Sun } from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeProvider";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { TooltipProvider } from "@/components/ui/tooltip";
import { JargonProvider } from "@/context/JargonContext";
import { JargonToggle } from "@/components/admin/JargonTerm";

import Sidebar, {
    useSidebarCollapsed,
    SIDEBAR_WIDTH_EXPANDED,
    SIDEBAR_WIDTH_COLLAPSED,
} from "@/components/Sidebar";
import ComplianceFooter from "@/components/landing/ComplianceFooter";
import NotificationBell from "@/components/NotificationBell";
import AdminInboxBell from "@/components/AdminInboxBell";
import { CommandPaletteTrigger } from "@/components/CommandPalette";
import PasswordChangeGate from "@/components/PasswordChangeGate";
import MobileBottomNav from "@/components/MobileBottomNav";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import Breadcrumb from "@/components/Breadcrumb";
import { OnboardingGateProvider, useOnboardingGate, GATE_ALLOWED_PATHS } from "@/context/OnboardingGateContext";
import { useAppCopy } from "@/lib/app-i18n";
import useSwipeToDismiss from "@/hooks/useSwipeToDismiss";
import { CurrencyToggleBar } from "@/components/CurrencyValue";

// Redirects away from any route the current onboarding stage doesn't allow —
// defense-in-depth against directly typing a locked URL (Sidebar/MobileBottomNav
// already prevent clicking into it, this catches the address-bar path too).
function OnboardingGateRedirect() {
    const { stage } = useOnboardingGate();
    const loc = useLocation();
    const nav = useNavigate();
    useEffect(() => {
        if (stage === "complete") return;
        const allowed = GATE_ALLOWED_PATHS[stage] || [];
        if (!allowed.includes(loc.pathname)) nav("/app", { replace: true });
    }, [stage, loc.pathname, nav]);
    return null;
}

export default function AppLayout({ children }) {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const nav = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const { collapsed, toggle: toggleSidebar } = useSidebarCollapsed();
    const { t } = useAppCopy();

    const closeMobileDrawer = useCallback(() => setMobileOpen(false), []);
    const swipeRef = useSwipeToDismiss({
        onDismiss: closeMobileDrawer,
        direction: "left",
        threshold: 60,
        enabled: mobileOpen,
    });

    const sidebarWidth = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
    const doLogout = useCallback(async () => { await logout(); nav("/"); }, [logout, nav]);

    return (
        <TooltipProvider delayDuration={150}>
        <JargonProvider>
        <OnboardingGateProvider>
        <OnboardingGateRedirect />
        <div className="min-h-screen flex flex-col bg-muted/30">

            {/* Desktop sidebar — fixed, animates width, flush to top */}
            <aside
                className="hidden lg:block fixed left-0 top-0 bottom-0 z-30"
                style={{ width: sidebarWidth }}
                data-testid="app-sidebar-slot"
            >
                <Sidebar
                    user={user}
                    collapsed={collapsed}
                    onToggle={toggleSidebar}
                    onLogout={doLogout}
                />
            </aside>

            {/* Content wrapper — padding-left animates with sidebar width via CSS var */}
            <div
                className="flex-1 flex flex-col min-w-0 md-app-content transition-[padding-left] duration-300 ease-in-out"
                style={{ "--md-sidebar-width": `${sidebarWidth}px` }}
                data-content-shift={sidebarWidth}
            >

                {/* Top header */}
                <header className="sticky top-0 z-20 h-14 border-b border-border bg-background/95 backdrop-blur-sm">
                    <div className="h-full px-4 md:px-6 flex items-center justify-between gap-3">
                        {/* Mobile hamburger */}
                        <div className="flex items-center gap-2 lg:hidden">
                            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                                <SheetTrigger asChild>
                                    <button
                                        className="p-2 rounded-md hover:bg-secondary"
                                        data-testid="mobile-menu-btn"
                                        aria-label={t("header.open_menu", "Open menu")}
                                    >
                                        <Menu className="w-5 h-5" strokeWidth={1.75} />
                                    </button>
                                </SheetTrigger>
                                <SheetContent
                                    side="left"
                                    className="p-0 border-none [&>button]:hidden"
                                    style={{ width: SIDEBAR_WIDTH_EXPANDED, background: "#922529" }}
                                >
                                    <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                                    <div
                                        ref={swipeRef}
                                        className="h-full w-full touch-pan-y"
                                        data-testid="mobile-drawer-swipe-zone"
                                    >
                                        <Sidebar
                                            user={user}
                                            collapsed={false}
                                            showToggle={false}
                                            onNavigate={closeMobileDrawer}
                                            onLogout={doLogout}
                                        />
                                    </div>
                                </SheetContent>
                            </Sheet>
                            <Link to="/app" className="flex items-center gap-2 lg:hidden">
                                <span
                                    className="inline-flex items-center justify-center rounded-lg font-heading font-bold tracking-wider shadow-sm"
                                    style={{ background: "#922529", color: "#FFFFFF", width: 24, height: 24, fontSize: 9, letterSpacing: "0.05em" }}
                                    aria-label="Melian Dialogue"
                                >
                                    MD
                                </span>
                            </Link>
                        </div>

                        {/* Command palette + breadcrumb — desktop header */}
                        <div className="hidden lg:flex flex-1 items-center gap-3 min-w-0">
                            <CommandPaletteTrigger />
                            <Breadcrumb />
                        </div>

                        <div className="flex items-center gap-1.5">
                            <CurrencyToggleBar className="mr-1" />
                            <div className="lg:hidden">
                                <CommandPaletteTrigger />
                            </div>
                            {user?.role === "admin" && <JargonToggle />}
                            {user?.role === "admin" && <AdminInboxBell />}
                            <NotificationBell />
                            <button
                                onClick={toggle}
                                data-testid="theme-toggle"
                                className="p-2 rounded-md hover:bg-secondary transition-colors"
                                aria-label={t("header.toggle_theme", "Toggle theme")}
                            >
                                {theme === "dark"
                                    ? <Sun className="w-4 h-4" strokeWidth={1.75} />
                                    : <Moon className="w-4 h-4" strokeWidth={1.75} />}
                            </button>
                        </div>
                    </div>
                </header>

                {/* Main content */}
                <main className="flex-1 w-full mx-auto px-3 sm:px-4 md:px-6 py-4 md:py-6 min-w-0 max-w-[1600px] pb-24 lg:pb-6">
                    {children ?? <Outlet />}
                </main>

                <PasswordChangeGate />
                <ComplianceFooter />
            </div>

            {/* Mobile-only bottom nav (PWA thumb zone) */}
            <MobileBottomNav user={user} />

            {/* PWA Add-to-Home-Screen prompt */}
            <PWAInstallPrompt />
        </div>
        </OnboardingGateProvider>
        </JargonProvider>
        </TooltipProvider>
    );
}
